var _react_refresh_temp_1;
_react_refresh_temp_1 = $RefreshSig$();
import { arrow, autoUpdate, flip, offset, safePolygon, shift, useClick, useDismiss, useFloating, useFocus, useHover, useInteractions, } from '@floating-ui/react';
import { useRef } from 'react';
/**
 * Position element relative to any other (even if it's not ancestor)
 * using JavaScript.
 *
 * Wraps around the Floating UI library and:
 * 1. Enables some sensible defaults for behaviors (Floating UI middleware)
 * 2. Provides an opinionated shorthand for configuring arrow
 */
export const useJSPositioning = ({ contentOffset, fallbackAxisSideDirection = 'none', hasArrow, placement, shouldToggleWhenFocused, strategy, ...floatingUiOptions } = {}) => {
    _react_refresh_temp_1();
    const arrowRef = useRef(null);
    const middleware = [
        Boolean(contentOffset) && offset(contentOffset),
        shift(),
        flip({
            fallbackAxisSideDirection,
        }),
        hasArrow &&
            arrow({
                element: arrowRef,
            }),
    ];
    const { context, floatingStyles, refs } = useFloating({
        ...floatingUiOptions,
        onOpenChange: (open, event, reason) => {
            // Keeps a parent native <dialog> from closing on the same Escape
            if (reason === 'escape-key') {
                event?.preventDefault();
            }
            floatingUiOptions.onOpenChange?.(open, event, reason);
        },
        ...(strategy && { strategy }),
        whileElementsMounted: autoUpdate,
        placement: placement || 'top',
        middleware: middleware.filter(Boolean),
        transform: false,
    });
    const hover = useHover(context, {
        enabled: Boolean(shouldToggleWhenFocused),
        move: false,
        /**
         * This "safe polygon" was an approach coined by Amazon:
         * https://bjk5.com/post/44698559168/breaking-down-amazons-mega-dropdown
         *
         * Documentation on the topic from Floating-UI:
         * - https://floating-ui.com/docs/usehover#safepolygon
         * - https://floating-ui.com/docs/react#interactions
         */
        handleClose: safePolygon(),
    });
    const focus = useFocus(context, {
        enabled: Boolean(shouldToggleWhenFocused),
    });
    const click = useClick(context, {
        enabled: !shouldToggleWhenFocused,
    });
    // Hover/focus content must close on Escape (WCAG 1.4.13)
    const dismiss = useDismiss(context, {
        outsidePress: !shouldToggleWhenFocused,
    });
    const { getFloatingProps, getReferenceProps } = useInteractions([
        hover,
        focus,
        click,
        // Escape is caught by useDismiss's document listener; keeps its onKeyDown off spread tooltip triggers
        shouldToggleWhenFocused ? { ...dismiss, reference: {} } : dismiss,
    ]);
    return {
        context,
        triggerProps: {
            // eslint-disable-next-line @typescript-eslint/unbound-method -- floating-ui ref setters are standalone callbacks that don't use `this`
            ref: refs.setReference,
            ...getReferenceProps(),
            // span and div elements are not focusable
            ...(hasArrow && { tabIndex: 0 }),
        },
        targetProps: {
            style: floatingStyles,
            // eslint-disable-next-line @typescript-eslint/unbound-method -- floating-ui ref setters are standalone callbacks that don't use `this`
            ref: refs.setFloating,
            ...getFloatingProps(),
        },
        arrowProps: {
            context,
            ref: arrowRef,
        },
        // anchorRef is separated from triggerProps since triggerProps are usually spread
        // eslint-disable-next-line @typescript-eslint/unbound-method -- unbound method - this binding doesn't matter
        anchorRef: refs.setPositionReference,
        refs,
    };
};
_react_refresh_temp_1(useJSPositioning, "Ak9VvgAQY2438Szty9tqBs22p1U=", false, () => [useFloating, useHover, useFocus, useClick, useDismiss, useInteractions]);
